import React, { useState, useRef } from "react";
import Container from "../components/container";
import { useTranslation } from 'react-i18next';
import { getProcedureTable } from "../data/procedureTypesData";
import AImg_nl from "../img/explanation/Type A - NL.png";
import AImgMobile_nl from "../img/explanation/Mobile-TypeA-NL.svg";
import EImg_nl from "../img/explanation/Type E - NL.png";
import EImgMobile_nl from "../img/explanation/Mobile-TypeE-NL.svg";
import DImg_nl from "../img/explanation/Type D - NL.png";
import DImgMobile_nl from "../img/explanation/Mobile-TypeD-NL.svg";
import AImg_fr from "../img/explanation/Type A - FR.png";
import AImgMobile_fr from "../img/explanation/Mobile-TypeA-FR.svg";
import EImg_fr from "../img/explanation/Type E - FR.png";
import EImgMobile_fr from "../img/explanation/Mobile-TypeE-FR.svg";
import DImg_fr from "../img/explanation/Type D - FR.png";
import DImgMobile_fr from "../img/explanation/Mobile-TypeD-FR.svg";
import AImg_en from "../img/explanation/Type A - EN.png";
import AImgMobile_en from "../img/explanation/Mobile-TypeA-EN.svg";
import EImg_en from "../img/explanation/Type E - EN.png";
import EImgMobile_en from "../img/explanation/Mobile-TypeE-EN.svg";
import DImg_en from "../img/explanation/Type D - EN.png";
import DImgMobile_en from "../img/explanation/Mobile-TypeD-EN.svg";

import {
    Card,
    CardBody,
} from "@material-tailwind/react";

function ExplanationPage() {
    const { t, i18n } = useTranslation();
    const procedureTable = getProcedureTable(t);
    const [activeTab, setActiveTab] = useState(procedureTable[0]?.tabValue || 'A'); // Default to first procedure

    const activeTabRef = useRef(null);
    const stickyHeaderRef = useRef(null);

    const handleProcedureClick = (tabValue) => {
        setActiveTab(tabValue);

        if (isScreenSizeSmallerThanLarge()) {
            setTimeout(() => {
                if (activeTabRef.current) {
                    const stickyHeight = stickyHeaderRef.current?.offsetHeight || 0;
                    const offset = 50 + stickyHeight + 2; // 50px pour la navbar + hauteur du sticky header
                    const elementPosition = activeTabRef.current.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    };

    const isScreenSizeSmallerThanLarge = () => {
        return (
            window.innerWidth < 1024 // 1024px = breakpoint 'lg' de Tailwind
        );
    };


    const imageSources = {
        nl: {
            A: isScreenSizeSmallerThanLarge() ? AImgMobile_nl : AImg_nl,
            E: isScreenSizeSmallerThanLarge() ? EImgMobile_nl : EImg_nl,
            D: isScreenSizeSmallerThanLarge() ? DImgMobile_nl : DImg_nl,
        },
        fr: {
            A: isScreenSizeSmallerThanLarge() ? AImgMobile_fr : AImg_fr,
            E: isScreenSizeSmallerThanLarge() ? EImgMobile_fr : EImg_fr,
            D: isScreenSizeSmallerThanLarge() ? DImgMobile_fr : DImg_fr,
        },
        en: {
            A: isScreenSizeSmallerThanLarge() ? AImgMobile_en : AImg_en,
            E: isScreenSizeSmallerThanLarge() ? EImgMobile_en : EImg_en,
            D: isScreenSizeSmallerThanLarge() ? DImgMobile_en : DImg_en,
        },
    };

    // Determine current language
    const currentLang = i18n.language;
    
    // Get the active procedure details
    const activeProcedure = procedureTable.find(proc => proc.tabValue === activeTab);

    return (
        <>
            <Container className="flex flex-col gap-6">
                <div className="font-bold text-3xl text-left text-blue-900 dark:text-yellow-800">
                    {t('exp-title')}
                </div>
                <div className="text-sm text-left font-normal text-gray-700 dark:text-white">
                    {t('exp-intro-body-1')}
                </div>
                <div className="text-sm text-left font-normal text-gray-700 dark:text-white italic">
                    {t('exp-intro-body-2')}
                </div>
                <div className="text-sm text-left font-normal text-gray-700 dark:text-white">
                    {t('exp-intro-body-3')}
                </div>
                <div className="text-sm text-left font-normal text-gray-700 dark:text-white">
                    {t('exp-intro-body-4')}
                </div>
                 
                {/* Sticky procedure selection */}
                <div ref={stickyHeaderRef} className="lg:static sticky top-[50px] z-10 backdrop-blur-md pt-2 pb-2">
                    <div className="container mx-auto">
                        <div className="flex items-center justify-center gap-4">
                            {procedureTable.map((proc, index) => (
                                <Card 
                                    key={index} 
                                    className={`cursor-pointer transition-all duration-300 ${
                                        activeTab === proc.tabValue 
                                        ? "border-2 border-blue-600 dark:border-yellow-800 shadow-lg" 
                                        : "hover:shadow-md"
                                    }`}
                                    onClick={() => handleProcedureClick(proc.tabValue)}>
                                    <CardBody className="text-center gap-2 p-4">
                                        <div className="font-bold text-sm text-blue-900 flex items-center">
                                            {proc.title}
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Main content - Desktop: flex-row, Mobile: flex-col */}
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Procedures cards container */}
                    <div className="lg:w-1/3 flex flex-col gap-4">
                        {procedureTable.map((proc, index) => (
                            <div key={index}>
                                {/* Procedure Card */}
                                <Card 
                                    ref={activeTab === proc.tabValue ? activeTabRef : null}
                                    className={`cursor-pointer transition-all duration-300 ${
                                        activeTab === proc.tabValue 
                                        ? "border-2 border-blue-600 dark:border-yellow-800 shadow-lg" 
                                        : "hover:shadow-md"
                                    }`}
                                    onClick={() => handleProcedureClick(proc.tabValue)}
                                >
                                    <CardBody className="text-left gap-2 p-4">
                                        <div className="font-bold text-xl text-blue-900 mb-2 flex items-center">
                                            {proc.title}
                                        </div>
                                        <ul className="list-disc list-inside">
                                            {proc.characteristics.map((characteristic, charIndex) => (
                                                <li key={charIndex} className="text-blue-gray-700 font-medium">
                                                    {characteristic}
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="text-blue-gray-400 font-medium mt-2">
                                            {proc.needs}
                                        </div>
                                        <div className="text-blue-gray-400 font-medium">
                                            {proc.delay}
                                        </div>
                                        <div className="text-blue-gray-400 font-medium">
                                            {proc.costs} <a href="/faq/fees/cost"><strong>FAQ</strong></a>
                                        </div>
                                    </CardBody>
                                </Card>
                                
                                {/* Image below the procedure - Only visible on mobile when active */}
                                {activeTab === proc.tabValue && (
                                    <div className="lg:hidden mt-4 mb-6">
                                        <div className="relative w-full">
                                            <div className=" top-4 left-0 w-full z-9 flex justify-center">
                                                <h2 className="text-2xl font-bold text-blue-900 dark:text-white px-4 py-2 rounded ">
                                                    {proc.title}
                                                </h2>
                                            </div>
                                            <img
                                                className="w-full h-full object-cover"
                                                src={imageSources[currentLang][proc.tabValue]}
                                                alt={`Procedure ${proc.tabValue} steps`}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Right side - Procedure image - Only visible on desktop */}
                    <div className="hidden lg:block lg:w-2/3 h-full">
                        <div className="relative w-full h-full">
                            <div className="absolute top-4 left-0 w-full z-9 flex justify-center">
                                <h2 className="text-2xl font-bold text-blue-900 bg-white/70 px-4 py-2 rounded">
                                    {activeProcedure?.title}
                                </h2>
                            </div>
                            <img
                                className="w-full h-full object-cover"
                                src={imageSources[currentLang][activeTab]}
                                alt={`Procedure ${activeTab} steps`}
                            />
                        </div>
                    </div>
                </div>
            </Container>
        </>
    );
}

export default ExplanationPage;